import { useRequiredContext } from "@redotech/react-util/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";

import { createContext, memo, useMemo } from "react";

/**
 * Tri-state describing how much of the Conversion Cloud a team can use.
 * - "none": team has no Conversion access at all (no secondary nav)
 * - "intent-only": self-serve teams that activated Intent (rescue popup) but
 *   have not been onboarded to the full suite by a sales rep. Most features
 *   appear locked with a schedule-a-call CTA.
 * - "full": admin has flipped the Conversion toggle on; everything unlocked.
 */
export type ConversionAccessLevel = "none" | "intent-only" | "full";

/**
 * Pure derivation of access level from the two underlying team flags.
 * Extracted so it can be unit-tested without mounting the React provider.
 *
 * "full" wins over "intent-only" when both flags are set — a team that's been
 * onboarded by sales should never show the locked UI even if intentRequested
 * was flipped earlier in their lifecycle.
 */
export function deriveConversionAccessLevel(input: {
  checkoutOptimizationEnabled: boolean;
  intentRequested: boolean;
}): ConversionAccessLevel {
  if (input.checkoutOptimizationEnabled) {
    return "full";
  }
  if (input.intentRequested) {
    return "intent-only";
  }
  return "none";
}

export interface ConversionSuiteStatuses {
  conversionEnabled: boolean;
  checkoutOptimizationEnabled: boolean;
  checkoutOutboundRatesEnabled: boolean;
  accessLevel: ConversionAccessLevel;
}

export const ConversionSuiteStatusesContext = createContext<
  ConversionSuiteStatuses | undefined
>(undefined);

export const ConversionSuiteStatusesProvider = memo(
  function ConversionSuiteStatusesProvider({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const team = useRequiredContext(TeamContext);
    const settings = team.settings;

    const checkoutOptimizationEnabled =
      !!settings.checkoutOptimization?.enabled;
    const checkoutOutboundRatesEnabled =
      !!team.shopifyIntegration?.checkoutOutboundRatesEnabled;
    const conversionEnabled =
      checkoutOptimizationEnabled && checkoutOutboundRatesEnabled;
    const intentRequested = !!settings.predictiveConversion?.intentRequested;

    const accessLevel = deriveConversionAccessLevel({
      checkoutOptimizationEnabled,
      intentRequested,
    });

    const conversionSuiteStatuses = useMemo(
      () => ({
        conversionEnabled,
        checkoutOptimizationEnabled,
        checkoutOutboundRatesEnabled,
        accessLevel,
      }),
      [
        conversionEnabled,
        checkoutOptimizationEnabled,
        checkoutOutboundRatesEnabled,
        accessLevel,
      ],
    );

    return (
      <ConversionSuiteStatusesContext.Provider value={conversionSuiteStatuses}>
        {children}
      </ConversionSuiteStatusesContext.Provider>
    );
  },
);

export { default as POST_PURCHASE_UPSELL_ICON } from "@redotech/redo-icons/cursor-click-01.svg";
export { default as POST_PURCHASE_UPSELL_ICON_FILLED } from "@redotech/redo-icons/cursor-click-01_filled.svg";
export { default as CONVERSION_ICON } from "@redotech/redo-icons/shopping-cart-03.svg";
export { default as CONVERSION_ICON_FILLED } from "@redotech/redo-icons/shopping-cart-03_filled.svg";
